import { KeyRound, Mail, User, UserPlus } from '@hostingportal/icons'
import { Input } from '@hostingportal/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { allowedDomainMessage, isAllowedEmail, writeAuthSession } from '../../lib/auth'
import { AuthError as SignInError, activateInvitedUser } from '../../lib/auth-provider'
import { Trans, t } from '../../lib/i18n'
import { isMock } from '../../lib/runtime'
import {
  AuthActionRow,
  AuthError,
  AuthExperience,
  AuthField,
  AuthForm,
  PasswordChecklist,
  PasswordField,
  authInputClassName,
  passwordIssue,
  passwordsMatch,
} from '../../modules/auth/auth-experience'

/**
 * Signup page. hosting.com CRM workspaces are invite-only; this page activates the
 * temporary Cognito password from the invite and replaces it with a permanent one.
 */
export const Route = createFileRoute('/_auth/signup')({
  component: SignupPage,
})

function SignupPage() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [temporaryPassword, setTemporaryPassword] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  return (
    <AuthExperience
      icon={UserPlus}
      title={<Trans>Activate your account</Trans>}
      description={<Trans>Use your invite password once, then set your password.</Trans>}
    >
      <AuthForm
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy) return
          const trimmedName = name.trim()
          const trimmedEmail = email.trim()
          if (!trimmedName) {
            setError(t('Enter your name.'))
            return
          }
          if (!isAllowedEmail(trimmedEmail)) {
            setError(t(allowedDomainMessage()))
            return
          }
          if (!temporaryPassword) {
            setError(t('Enter the temporary password from your invite.'))
            return
          }
          const passwordError = passwordIssue(password)
          if (passwordError) {
            setError(passwordError)
            return
          }
          if (!passwordsMatch(password, confirmPassword)) {
            setError(t('Passwords must match.'))
            return
          }
          if (isMock) {
            writeAuthSession({ email: trimmedEmail, name: trimmedName })
            navigate({ to: '/' })
            return
          }
          setBusy(true)
          try {
            const session = await activateInvitedUser({
              email: trimmedEmail,
              temporaryPassword,
              newPassword: password,
            })
            writeAuthSession({ ...session, name: session.name ?? trimmedName })
            navigate({ to: '/' })
          } catch (err) {
            setError(
              err instanceof SignInError
                ? t(err.message)
                : t('Could not activate this account. Check the invite and try again.'),
            )
          } finally {
            setBusy(false)
          }
        }}
      >
        <AuthField label={<Trans>Name</Trans>} icon={User}>
          <Input
            placeholder={t('Your name')}
            aria-label={t('Name')}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (error) setError(null)
            }}
            invalid={Boolean(error) && !name.trim()}
            autoComplete="name"
            className={authInputClassName}
            required
          />
        </AuthField>
        <AuthField label={<Trans>Work email</Trans>} icon={Mail}>
          <Input
            type="email"
            placeholder={t('you@company.com')}
            aria-label={t('Work email')}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError(null)
            }}
            invalid={Boolean(error) && !isAllowedEmail(email)}
            autoComplete="email"
            className={authInputClassName}
            required
          />
        </AuthField>
        <AuthField label={<Trans>Temporary password</Trans>} icon={KeyRound}>
          <Input
            type="password"
            placeholder={t('From your invite email')}
            aria-label={t('Temporary password')}
            value={temporaryPassword}
            onChange={(e) => {
              setTemporaryPassword(e.target.value)
              if (error) setError(null)
            }}
            invalid={Boolean(error) && !temporaryPassword}
            autoComplete="one-time-code"
            className={authInputClassName}
            required
          />
        </AuthField>
        <PasswordField
          label={<Trans>New password</Trans>}
          value={password}
          onChange={(value) => {
            setPassword(value)
            if (error) setError(null)
          }}
          invalid={Boolean(error) && Boolean(passwordIssue(password))}
          autoComplete="new-password"
        />
        <PasswordField
          label={<Trans>Confirm password</Trans>}
          value={confirmPassword}
          onChange={(value) => {
            setConfirmPassword(value)
            if (error) setError(null)
          }}
          invalid={Boolean(error) && !passwordsMatch(password, confirmPassword)}
          autoComplete="new-password"
          placeholder={t('Confirm password')}
          ariaLabel={t('Confirm password')}
        />
        <PasswordChecklist password={password} confirmPassword={confirmPassword} />
        {error ? <AuthError>{error}</AuthError> : null}
        <AuthActionRow mode="signup" busy={busy} />
      </AuthForm>
    </AuthExperience>
  )
}
